import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Page } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bot, Send, Sparkles, User } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "O que devo comprar essa semana?",
  "Quais produtos estão parados há mais tempo?",
  "Qual a previsão de receita do próximo mês?",
  "Sugira uma estratégia de promoção para liquidar estoque parado.",
];

export default function Copilot() {
  const [provider, setProvider] = useState("anthropic");
  const [messages, setMessages] = useState([
    { id: "welcome", role: "assistant", content: "Olá! Sou o Copiloto IA da SmartStockAI. Posso te ajudar a tomar decisões sobre compras, promoções e prevenir rupturas. O que você quer saber?" },
  ]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (preset) => {
    const message = (preset || text).trim();
    if (!message || sending) return;
    setMessages((m) => [...m, { id: `u-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, role: "user", content: message }]);
    setText("");
    setSending(true);
    try {
      const r = await api.post("/copilot/chat", { message, provider, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, role: "assistant", content: r.data.reply, model: r.data.model }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao consultar IA");
    } finally {
      setSending(false);
    }
  };

  return (
    <Page
      title="Copiloto IA"
      subtitle="Pergunte em português sobre seu estoque. Recomendações práticas em segundos."
      action={
        <div className="w-44">
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger data-testid="copilot-model-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="anthropic">Claude Sonnet 4.5</SelectItem>
              <SelectItem value="openai">GPT-5.2</SelectItem>
              <SelectItem value="gemini">Gemini 3 Flash</SelectItem>
            </SelectContent>
          </Select>
        </div>
      }
    >
      <div className="rounded-xl border border-border bg-card flex flex-col h-[calc(100vh-280px)] min-h-[500px]">
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {messages.map((m, i) => (
            <div key={m.id || `m-${i}`} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "assistant" && (
                <div className="w-8 h-8 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
              )}
              <div data-testid={`msg-${m.role}-${i}`} className={`max-w-[80%] p-4 rounded-xl whitespace-pre-wrap text-sm leading-relaxed ${
                m.role === "user" ? "bg-primary text-white" : "bg-secondary border border-border"
              }`}>
                {m.content}
                {m.model && <div className="text-[10px] text-muted-foreground mt-2 font-mono">{m.model}</div>}
              </div>
              {m.role === "user" && (
                <div className="w-8 h-8 rounded-full bg-secondary border border-border flex items-center justify-center shrink-0">
                  <User className="w-4 h-4" />
                </div>
              )}
            </div>
          ))}
          {sending && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0 pulse-coral">
                <Bot className="w-4 h-4 text-primary" />
              </div>
              <div className="p-4 rounded-xl bg-secondary border border-border text-sm text-muted-foreground">Pensando...</div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {messages.length === 1 && (
          <div className="px-6 pb-3 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} data-testid={`suggestion-${s.slice(0, 12)}`} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full border border-border bg-secondary hover:border-primary/40 hover:text-primary transition">
                {s}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border p-4 flex gap-2">
          <Input data-testid="copilot-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Pergunte ao copiloto..." disabled={sending} />
          <Button data-testid="copilot-send-btn" type="submit" disabled={sending || !text.trim()} className="bg-primary hover:bg-primary/90">
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </Page>
  );
}
